import React from 'react';
import cn from 'classnames';

import { LIVE_SPORTEVENT_STATUSES } from 'app-constants';
import {
    GameLogo,
    MarketsCount,
    SmartLink,
    SportEventInViewSubscription,
    SportEventSeoMicromarking,
} from 'components';
import favorite from 'modules/favorite';
import matchList from 'modules/match-list';
import { Action, AnalyticAttrsCtx, Category } from 'services/analytics';
import DateTime from '../DateTime';
import useMatchRow from './useMatchRow';

import './matchRow.scss';

interface Props {
    matchId: string;
    countRenderMarkets: number;
    hasMatchHeader: boolean;
}

const {
    components: { TopMarketsContainer },
} = matchList;

const {
    components: { ToggleFavoriteButton },
} = favorite;

const ANALYTICS = {
    'data-category': Category.TopSportEventsCenter,
};

const ANALYTICS_MARKETS = {
    'data-category': Category.TopSportEventsCenter,
};

const MatchRow: React.FC<Props> = ({
    matchId,
    countRenderMarkets,
    hasMatchHeader,
}) => {
    const { match, linkToMatch, competitors, isOddStatVisible } = useMatchRow({
        matchId,
    });

    if (!match) return null;

    const { sportId, startTime, marketsCount, status, version } = match;

    const isLive = LIVE_SPORTEVENT_STATUSES.includes(status);

    return (
        <AnalyticAttrsCtx.Provider value={ANALYTICS}>
            <div
                styleName={cn('container', {
                    'is-one-market-view': countRenderMarkets === 1,
                    'is-odd-stat-visible': isOddStatVisible,
                })}
            >
                <SportEventSeoMicromarking sportEventId={matchId} />
                <SportEventInViewSubscription
                    matchId={matchId}
                    version={version}
                />
                <div styleName="favorite">
                    <ToggleFavoriteButton matchId={matchId} />
                </div>

                <div styleName="logo">
                    <GameLogo gameId={sportId} />
                </div>

                <div styleName="info">
                    <SmartLink styleName="competitors" to={linkToMatch}>
                        <div
                            data-category={Category.TopSportEventsCenter}
                            data-action={Action.ClickOnTitle}
                            styleName="competitor"
                        >
                            {competitors.homeName}
                        </div>
                        <div
                            data-category={Category.TopSportEventsCenter}
                            data-action={Action.ClickOnTitle}
                            styleName="competitor"
                        >
                            {competitors.awayName}
                        </div>
                    </SmartLink>

                    <div styleName="date">
                        <DateTime
                            startTime={startTime}
                            isLive={isLive}
                            matchId={matchId}
                        />
                    </div>
                </div>

                <div styleName="markets">
                    <AnalyticAttrsCtx.Provider value={ANALYTICS_MARKETS}>
                        <TopMarketsContainer
                            hasVS={true}
                            isDefaultName={true}
                            matchId={matchId}
                            hasMatchHeader={hasMatchHeader}
                            sportId={sportId}
                            count={countRenderMarkets}
                            withStubs={true}
                        />
                    </AnalyticAttrsCtx.Provider>
                </div>

                <MarketsCount
                    marketsCount={marketsCount}
                    topMarketsCount={countRenderMarkets}
                    linkToMatch={linkToMatch}
                />
            </div>
        </AnalyticAttrsCtx.Provider>
    );
};

export default React.memo(MatchRow);
